import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/router";
import { BuilderIOUtils } from "../utils/BuilderIOUtils";
import Cookies from "js-cookie";
import { PublicSettings } from "../config/PublicSettings";

export const useBuilderNavigation = ({ isBuilderPage }: { isBuilderPage: boolean }) => {
    const router = useRouter();
    const builder = BuilderIOUtils.builder;

    const [contentId, setContentId] = useState<string | undefined>(undefined);
    const [variationId, setVariationId] = useState<string | undefined>(undefined);

    // Track pages used to provide some sort of service, app or corporate info separately
    // Util clicks should only register as a single event, and should take precedence over other events
    const UTILITY_CLICK_EVENT = "cta-click-utility";
    const isUtilUrl = useCallback((url: string) => {
        const utilUrls = [
            "/employers",
            "/agency-info",
            "/about-us",
            "/careers",
            "/member-center",
            PublicSettings.MEMBER_CENTER_URL_ROOT,
            "/contact",
            "/claims-form",
            "/newsroom",
            "/news",
            "https://help.spotpet.com/",
            "https://www.linkedin.com/",
            "tel:"
        ];
        return utilUrls.some(u => url.startsWith(u));
    }, []);

    useEffect(() => {
        if (isBuilderPage) {
            const visitorId = builder.getVisitorId();
            if (visitorId) {
                Cookies.set("builderVisitorId", visitorId);
            }
        }
    }, [builder, isBuilderPage]);

    useEffect(() => {
        const getContentIds = async () => {
            try {
                const content = await builder.getContent(PublicSettings.BUILDER_IO_PAGE_MODEL).promise();

                if (!!content.length) {
                    const entry = content[0];
                    setContentId(entry.id);
                    if (entry?.testVariationId) {
                        setVariationId(entry.testVariationId);
                    }
                }
            } catch (error) {
                console.error("Error fetching content: ", error);
            }
        };
        if (isBuilderPage) {
            getContentIds();
        }
    }, [builder, isBuilderPage]);

    useEffect(() => {
        const handleExternalNavigation = (anchor: HTMLAnchorElement) => {
            const href = anchor.getAttribute("href");
            if (!href) return;

            const isExternalLink =
                !href.startsWith("/") && !href.startsWith("#") && !href.startsWith(`${window.location.origin}`) && !href.startsWith("tel:") && !href.startsWith("mailto:");

            // Some util URLs are external, but should be tracked as utility clicks
            if (isUtilUrl(href) && !href.startsWith("/") && (href.startsWith("tel:") || !href.startsWith(`${window.location.origin}`))) {
                try {
                    builder.track(UTILITY_CLICK_EVENT);
                } catch (error) {
                    console.error("Error tracking external utility link click:", error);
                }
            } else if (isExternalLink) {
                try {
                    builder.track("cta-click-outbound");
                } catch (error) {
                    console.error("Error tracking external link click:", error);
                }
            }
        };

        const handleLinkClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const anchor = target.closest("a");
            if (!anchor) return;
            handleExternalNavigation(anchor);
        };

        const handleRouteChangeStart = (url: string) => {
            try {
                const isQuotePath = url === "/quote" || url.startsWith("/quote?");
                let eventType = isQuotePath ? "cta-click-check-price" : "cta-click-subpage";
                if (isUtilUrl(url)) {
                    eventType = UTILITY_CLICK_EVENT;
                }
                builder.track(eventType);
                if (contentId) {
                    builder.trackInteraction(contentId, variationId);
                }
            } catch (error) {
                console.error("Error tracking route change:", error);
            }
        };

        if (!isBuilderPage) return;

        document.addEventListener("click", handleLinkClick);
        router.events.on("routeChangeStart", handleRouteChangeStart);

        return () => {
            document.removeEventListener("click", handleLinkClick);
            router.events.off("routeChangeStart", handleRouteChangeStart);
        };
    }, [builder, contentId, isBuilderPage, router, variationId, isUtilUrl]);
};
